import { useState, useEffect } from 'react'
import i18next from 'i18next'
import { last, uniqBy } from 'lodash'

import { useInfinityScrollSimple } from 'hooks'
import { MODAL_MODE } from './config'
import { useListChatableUserFollowingsQuery } from 'store/api/yay/user'
import useChatRoomLogic from 'features/Chat/hocs/useChatRoomLogic'
import { Link } from 'features/i18n/router'
import usePageModal from 'features/Modal/hooks/usePageModal'
import { logEvent } from 'lib/analytics'
import { ROUTE_PATH } from 'lib/paths'
import { User } from 'lib/_types'

import Loading from 'components/Loading'
import Avatar from 'components/Avatar'
import Nickname from 'components/Nickname'
import LoadMore from 'components/LoadMore'
import Button from 'components/Button'

let searchTO = null

const NewChatModal = () => {
  const { generatePageModalUrl } = usePageModal()

  const [__search, __searchSet] = useState('')
  const [__lastTimestamp, __lastTimestampSet] = useState<string | number>('0')
  const [__userFollowings, __userFollowingsSet] = useState<Array<User>>([])
  const [__userFollowingsSearch, __userFollowingsSearchSet] = useState<
    Array<User>
  >([])

  const isSearching = __search?.length > 0
  const users = isSearching ? __userFollowingsSearch : __userFollowings

  const { onCreateChatRoom } = useChatRoomLogic()

  const usersFollowingsRes = useListChatableUserFollowingsQuery(
    isSearching ? { nickname: __search } : { from_timestamp: __lastTimestamp }
  )

  const loadMore = () => {
    __lastTimestampSet(last(users ?? [])?.last_loggedin_at)
  }

  const { loadMoreRef } = useInfinityScrollSimple({
    loadMore,
  })

  useEffect(() => {
    __userFollowingsSearchSet([])

    if (isSearching) {
      __userFollowingsSearchSet(usersFollowingsRes?.data?.users ?? [])
    } else {
      __userFollowingsSet(
        uniqBy(
          [
            ...__userFollowings?.map?.((x) => {
              return (
                usersFollowingsRes?.data?.users?.find?.(
                  (y) => y?.id == x?.id
                ) ?? x
              )
            }),
            ...(usersFollowingsRes?.data?.users ?? []),
          ],
          (x) => x?.id
        )
      )
    }
  }, [usersFollowingsRes])

  return (
    <div className="Modal__wrapper">
      <div className="Modal__header">
        <Link
          className="Modal__header__close Modal__header__close--back"
          to={generatePageModalUrl(MODAL_MODE.CHAT_LIST)}
        />

        <h2 className="Modal__header__h2">{i18next.t('common.chat')}</h2>
      </div>

      <div className="Modal__search">
        <div className="Search">
          <input
            className="Search__input"
            placeholder={i18next.t('new_chat_modal.search.placeholder')}
            type="text"
            onChange={(e) => {
              window.clearTimeout(searchTO)
              searchTO = window.setTimeout(() => {
                __searchSet(e?.target?.value)
              }, 400)
            }}
          />
        </div>
      </div>

      <div className="Modal__body">
        {usersFollowingsRes?.isLoading && <Loading />}

        {usersFollowingsRes?.isSuccess && (users?.length ?? 0) == 0 && (
          <div className="Placeholder Placeholder--recommend-users">
            {isSearching ? (
              <p className="Placeholder__p">No users with this username.</p>
            ) : (
              <p
                className="Placeholder__p"
                dangerouslySetInnerHTML={{
                  __html: i18next.t('new_chat_modal.not_found_placeholder'),
                }}
              />
            )}
          </div>
        )}

        {users?.length > 0 && (
          <dl className="RecommendUsers">
            {users?.map?.((user) => (
              <label className="RecommendUsers__item" key={user.id}>
                <Link
                  key={user.id}
                  to={ROUTE_PATH.USER(user?.id)}
                  onClick={() =>
                    logEvent.user_profile_open({
                      target_user_id: String(user?.id),
                      from: 'undefined',
                    })
                  }
                >
                  <Avatar user={user} showOnlineStatus />
                </Link>

                <div className="RecommendUsers__item__info">
                  <h3 className="RecommendUsers__item__h3">
                    <Nickname user={user} />
                  </h3>
                </div>

                <Button
                  buttonStyle="green"
                  icon="chat"
                  onClick={() => {
                    onCreateChatRoom({
                      userId: user.id,
                      from: 'follow_recommendations',
                    })
                  }}
                >
                  {i18next.t('common.chat')}
                </Button>
              </label>
            ))}

            {!isSearching && (
              <LoadMore
                isLoading={usersFollowingsRes?.isFetching}
                hidden={!usersFollowingsRes?.data?.users?.length}
                className="RecommendUsers__load-more"
                onClick={() => {
                  loadMore()
                }}
                ref={loadMoreRef}
              />
            )}
          </dl>
        )}
      </div>
    </div>
  )
}

export default NewChatModal
